"use client";

import { formatDistanceToNow } from "date-fns";
import { HardDrive, Info } from "lucide-react";
import { Checkbox } from "@repo/ui";
import { cn } from "@/lib/utils";
import type { PyloMedia } from "@/services/pylo-media.service";
import { MediaThumb } from "./media-thumb";
import { MediaUploadOverlay } from "./media-upload-overlay";
import { formatFileSize } from "./media-utils";
import type { MediaViewMode } from "./media-toolbar";
import type { MediaUpload } from "@/hooks/use-media-upload";

interface MediaResultsProps {
  files: Array<PyloMedia>;
  /** In-flight uploads, rendered as cards ahead of the results they will join. */
  uploads?: Array<MediaUpload>;
  view: MediaViewMode;
  activeId?: string;
  selectedIds: Set<string>;
  isLoading: boolean;
  /**
   * In `pick` a click is the selection gesture, so inspecting moves to its own
   * affordance; in `browse` a click opens the inspector and the checkbox selects.
   */
  mode?: "browse" | "pick";
  /** Show the per-card inspect button — for layouts with no inspector column. */
  showInspectAction?: boolean;
  onActivate: (media: PyloMedia) => void;
  onToggleSelect: (id: string) => void;
}

const uploadedLabel = (media: PyloMedia) => {
  if (!media.created_at) return "";
  const parsed = new Date(media.created_at);
  return Number.isNaN(parsed.getTime()) ? "" : `${formatDistanceToNow(parsed)} ago`;
};

const GridCard = ({
  media,
  isActive,
  isSelected,
  isPicking,
  showInspectAction,
  onActivate,
  onToggleSelect,
}: {
  media: PyloMedia;
  isActive: boolean;
  isSelected: boolean;
  isPicking: boolean;
  showInspectAction: boolean;
  onActivate: () => void;
  onToggleSelect: () => void;
}) => (
  // A div, not a button: the checkbox and the inspect action are real buttons, and
  // HTML forbids nesting them inside one. Matches ListRow.
  <div
    role="button"
    tabIndex={0}
    onClick={isPicking ? onToggleSelect : onActivate}
    onKeyDown={(event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      (isPicking ? onToggleSelect : onActivate)();
    }}
    className={cn(
      "group relative flex cursor-pointer flex-col overflow-hidden rounded-lg border bg-white text-left transition-shadow hover:shadow-sm",
      isSelected && isPicking && "ring-2 ring-primary",
      isActive && !isPicking && "ring-2 ring-primary",
    )}
  >
    <div
      className={cn(
        "absolute left-2 top-2 z-10 transition-opacity",
        isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100",
      )}
      onClick={(event) => {
        event.stopPropagation();
        onToggleSelect();
      }}
    >
      <Checkbox checked={isSelected} className="bg-white" />
    </div>

    {showInspectAction && (
      <button
        type="button"
        aria-label={`Details for ${media.file_name}`}
        className="absolute right-2 top-2 z-10 rounded-md bg-white/90 p-1 opacity-0 shadow-sm transition-opacity hover:bg-white group-hover:opacity-100"
        onClick={(event) => {
          event.stopPropagation();
          onActivate();
        }}
      >
        <Info className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    )}

    <div className="relative aspect-square w-full overflow-hidden bg-gray-50">
      <MediaThumb media={media} />
    </div>

    <div className="flex flex-col gap-0.5 px-2.5 py-2">
      <span className="truncate text-[13px] font-medium" title={media.file_name}>
        {media.file_name}
      </span>
      <span className="text-[11px] tabular-nums text-muted-foreground">
        {formatFileSize(media.file_size_bytes)}
      </span>
    </div>
  </div>
);

const ListRow = ({
  media,
  isActive,
  isSelected,
  isPicking,
  onActivate,
  onToggleSelect,
}: {
  media: PyloMedia;
  isActive: boolean;
  isSelected: boolean;
  isPicking: boolean;
  onActivate: () => void;
  onToggleSelect: () => void;
}) => (
  <div
    role="button"
    tabIndex={0}
    onClick={isPicking ? onToggleSelect : onActivate}
    onKeyDown={(event) => event.key === "Enter" && (isPicking ? onToggleSelect() : onActivate())}
    className={cn(
      "flex cursor-pointer items-center gap-3 border-b px-3 py-2 text-[13px] hover:bg-muted/50",
      isActive && !isPicking && "bg-muted/60",
      isSelected && isPicking && "bg-muted/60",
    )}
  >
    <div onClick={(event) => event.stopPropagation()}>
      <Checkbox checked={isSelected} onCheckedChange={onToggleSelect} />
    </div>
    <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded bg-gray-50">
      <MediaThumb media={media} iconClassName="h-4 w-4" />
    </div>
    <span className="min-w-0 flex-1 truncate font-medium" title={media.file_name}>
      {media.file_name}
    </span>
    <span className="w-40 shrink-0 truncate text-[12px] text-muted-foreground">
      {media.file_mime_type}
    </span>
    <span className="w-20 shrink-0 text-right text-[12px] tabular-nums text-muted-foreground">
      {formatFileSize(media.file_size_bytes)}
    </span>
    <span className="w-28 shrink-0 text-right text-[12px] text-muted-foreground">
      {uploadedLabel(media)}
    </span>
  </div>
);

const UPLOAD_STATUS_LABELS: Partial<Record<MediaUpload["status"], string>> = {
  creating: "Preparing…",
  processing: "Processing…",
  error: "Failed",
};

const uploadState = (upload: MediaUpload) => ({
  status: upload.status,
  progress: upload.progress,
  error: upload.error,
  uploadedBytes: upload.uploadedBytes,
  uploadSpeed: upload.uploadSpeed,
  eta: upload.eta,
  sizeBytes: upload.file.size,
});

const UploadCard = ({ upload }: { upload: MediaUpload }) => (
  <div
    className={cn(
      "relative flex flex-col overflow-hidden rounded-lg border bg-white text-left",
      upload.error && "border-destructive/40",
    )}
  >
    <div className="relative aspect-square w-full overflow-hidden bg-gray-50">
      <MediaThumb media={{ url: upload.preview, file_mime_type: upload.file.type }} />
      <MediaUploadOverlay {...uploadState(upload)} />
    </div>

    <div className="flex flex-col gap-0.5 px-2.5 py-2">
      <span className="truncate text-[13px] font-medium" title={upload.file.name}>
        {upload.file.name}
      </span>
      <span className="text-[11px] tabular-nums text-muted-foreground">
        {formatFileSize(upload.file.size)}
      </span>
    </div>
  </div>
);

const UploadRow = ({ upload }: { upload: MediaUpload }) => (
  <div className="flex items-center gap-3 border-b px-3 py-2 text-[13px]">
    <Checkbox checked={false} disabled />
    <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded bg-gray-50 opacity-60">
      <MediaThumb
        media={{ url: upload.preview, file_mime_type: upload.file.type }}
        iconClassName="h-4 w-4"
      />
    </div>
    <span className="min-w-0 flex-1 truncate font-medium" title={upload.file.name}>
      {upload.file.name}
    </span>
    <span className="w-40 shrink-0 truncate text-[12px] text-muted-foreground">
      {upload.file.type}
    </span>
    <span className="w-20 shrink-0 text-right text-[12px] tabular-nums text-muted-foreground">
      {formatFileSize(upload.file.size)}
    </span>
    <span className="w-28 shrink-0 text-right text-[12px] tabular-nums text-muted-foreground">
      {UPLOAD_STATUS_LABELS[upload.status] ?? `${upload.progress}%`}
    </span>
  </div>
);

export const MediaResults = ({
  files,
  uploads = [],
  view,
  activeId,
  selectedIds,
  isLoading,
  mode = "browse",
  showInspectAction = false,
  onActivate,
  onToggleSelect,
}: MediaResultsProps) => {
  const isPicking = mode === "pick";

  if (!isLoading && files.length === 0 && uploads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-muted-foreground">
        <HardDrive className="h-8 w-8 opacity-40" />
        <p className="text-[13px]">No files match these filters.</p>
      </div>
    );
  }

  if (view === "list") {
    return (
      <div className="overflow-hidden rounded-lg border bg-white">
        {uploads.map((upload) => (
          <UploadRow key={upload.key} upload={upload} />
        ))}
        {files.map((media) => (
          <ListRow
            key={media.id}
            media={media}
            isActive={activeId === media.id}
            isSelected={selectedIds.has(media.id)}
            isPicking={isPicking}
            onActivate={() => onActivate(media)}
            onToggleSelect={() => onToggleSelect(media.id)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
      {uploads.map((upload) => (
        <UploadCard key={upload.key} upload={upload} />
      ))}
      {files.map((media) => (
        <GridCard
          key={media.id}
          media={media}
          isActive={activeId === media.id}
          isSelected={selectedIds.has(media.id)}
          isPicking={isPicking}
          showInspectAction={showInspectAction}
          onActivate={() => onActivate(media)}
          onToggleSelect={() => onToggleSelect(media.id)}
        />
      ))}
    </div>
  );
};
